import React from 'react';
import { Paper, Stack, Tab, Tabs, Typography } from '@mui/material';

import TuningAdminTable, { safePreview } from '../tuning/TuningAdminTable';
import RagPipelineProfilesPanel from './RagPipelineProfilesPanel';
import RagPipelinesPanel from './RagPipelinesPanel';

import {
  EntityTypeFields,
  IngestionRunFields,
  PurposeFields,
  RoutingMappingFields,
} from './ragAdminConstants';

import {
  entityTypeColumns,
  ingestionRunColumns,
  routingMappingColumns,
} from './ragAdminUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { ADMIN_URL_TOOLBOX_CONFIG } from '../../PageStructure/admin-values';

const CONFIG_TABLE_TABS = [
  'pipelines',
  'pipeline-profiles',
  'routing-mappings',
  'entity-types',
  'pipeline-purposes',
  'ingestion-runs',
]

type ConfigTab = typeof CONFIG_TABLE_TABS[number];

const RagConfigTables: React.FC = () => {
  const navigate = useNavigate();
  const { tab3 } = useParams();
  const tab = CONFIG_TABLE_TABS.includes(tab3) ? tab3 : CONFIG_TABLE_TABS[0];
  const setTab = (tabName: string) => navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/ai/rag/config/${tabName}`);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          RAG Configuration
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Manage concrete RAG pipelines, blue/green pipeline profiles, generalized routing mappings,
          documentation entity types, pipeline purposes, and ingestion reporting.
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="pipelines" label="Concrete Pipelines + Knowledge Base" />
          <Tab value="pipeline-profiles" label="Profiles / Slots" />
          <Tab value="routing-mappings" label="Routing Mappings" />
          <Tab value="entity-types" label="Entity Types" />
          <Tab value="pipeline-purposes" label="Pipeline Purposes" />
          <Tab value="ingestion-runs" label="Ingestion Runs" />
        </Tabs>
      </Paper>

      {tab === 'pipelines' ? <RagPipelinesPanel /> : null}

      {tab === 'pipeline-profiles' ? <RagPipelineProfilesPanel /> : null}

      {tab === 'routing-mappings' ? (
        <TuningAdminTable
          title="Generalized RAG Routing Mappings"
          description="Map products, GTMs, product features, product components, product use cases, security use cases, security use case components, solution components, type defaults, and global defaults to pipeline profiles."
          endpoint="db/rag-routing-mapping/"
          queryKey={['rag-routing-mapping']}
          fields={RoutingMappingFields}
          defaultValues={{
            scope_type: 'product',
            scope_id: '',
            entity_type: null,
            purpose: null,
            pipeline_group: '',
            priority: 100,
            is_default: false,
            is_active: true,
            meta_filters: {},
            notes: '',
            updated_by: 'admin-ui',
          }}
          orderBy="priority"
          rowTitle={(row) => `${row.scope_type}:${row.scope_id || 'default'}`}
          rowSubtitle={(row) =>
            `${row.pipeline_group_key || row.pipeline_group_name || row.pipeline_group}`
          }
          columns={routingMappingColumns}
        />
      ) : null}

      {tab === 'entity-types' ? (
        <TuningAdminTable
          title="Documentation Entity Types"
          description="Canonical entity registry. Entity support flags indicate which workflows can use the entity."
          endpoint="db/documentation-entity-type/"
          queryKey={['documentation-entity-type']}
          fields={EntityTypeFields}
          defaultValues={{
            key: 'product_feature',
            label: '',
            description: '',
            supports_entity_documentation: true,
            supports_snackable_course: true,
            queue_task_type: 'entity_documentation_generation',
            snackable_queue_task_type: 'snackable_course_generation',
            canonical_model_name: '',
            is_active: true,
            sort_order: 100,
            updated_by: 'admin-ui',
          }}
          orderBy="sort_order"
          rowTitle={(row) => row.label || row.key}
          rowSubtitle={(row) => safePreview(row.description)}
          columns={entityTypeColumns}
        />
      ) : null}

      {tab === 'pipeline-purposes' ? (
        <TuningAdminTable
          title="RAG Pipeline Purposes"
          description="Purpose keys used by routing resolution, for example chat, doc_export, entity_documentation, snackable_course, and validation."
          endpoint="db/rag-pipeline-purpose/"
          queryKey={['rag-pipeline-purpose']}
          fields={PurposeFields}
          defaultValues={{
            key: '',
            label: '',
            description: '',
            is_active: true,
            sort_order: 100,
            updated_by: 'admin-ui',
          }}
          orderBy="sort_order"
          rowTitle={(row) => row.label || row.key}
          rowSubtitle={(row) => row.key}
        />
      ) : null}

      {tab === 'ingestion-runs' ? (
        <TuningAdminTable
          title="RAG Pipeline Ingestion Runs"
          description="Reporting and audit trail for doc-export and RAG ingestion activity."
          endpoint="db/rag-pipeline-ingestion-run/"
          queryKey={['rag-pipeline-ingestion-run']}
          fields={IngestionRunFields}
          defaultValues={{
            pipeline_group: '',
            pipeline: null,
            pipeline_slot: null,
            target_type: 'global',
            product: null,
            go_to_market: null,
            status: 'pending',
            trigger_source: 'api',
            job_id: '',
            execution_id: '',
            manifest_s3_bucket: '',
            manifest_s3_key: '',
            uploaded_files_count: 0,
            deleted_files_count: 0,
            skipped_files_count: 0,
            request_payload: {},
            response_payload: {},
            error_message: '',
            updated_by: 'admin-ui',
          }}
          orderBy="-created"
          allowCreate={false}
          allowEdit
          allowDelete
          defaultPageSize={25}
          rowTitle={(row) => `Run ${row.id}`}
          rowSubtitle={(row) =>
            `${row.pipeline_group_key || row.pipeline_group} / ${row.status}`
          }
          columns={ingestionRunColumns}
        />
      ) : null}
    </Stack>
  );
};

export default RagConfigTables;